/**
 * Deserializer - restores game state from saved data
 */

import type { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { FactionManager } from '@/game/factions/FactionManager';
import type { TechSystem } from '@/game/tech/TechSystem';
import type { VisionSystem } from '@/game/systems/VisionSystem';
import type { AIController } from '@/game/ai/AIController';
import { createUnitEntity } from '@/game/entities/createUnit';
import { createBuildingEntity } from '@/game/entities/createBuilding';
import type { HealthComponent } from '@/game/components/Health';
import type { SpawnerComponent } from '@/game/components/Spawner';
import { useGameStore } from '@/state/gameStore';
import type { SaveData, SavedEntity } from './SaveData';
import type { FactionId, UnitType, BuildingType } from '@/types';

export class Deserializer {
  /**
   * Restore game state from save data
   */
  static deserialize(
    saveData: SaveData,
    world: World,
    factionManager: FactionManager,
    techSystem: TechSystem,
    visionSystem: VisionSystem,
    aiController: AIController
  ): boolean {
    try {
      // Clear existing state
      world.clear();

      // Restore game state
      const store = useGameStore.getState();
      store.setGameTime(saveData.gameTime);
      store.setGameSpeed(saveData.gameSpeed);
      store.setPlayerFaction(saveData.playerFaction);
      store.setPlayerGold(saveData.playerGold);
      store.setDifficulty(saveData.config.difficulty);

      // Restore faction states
      this.restoreFactionStates(saveData, factionManager);

      // Restore entities
      this.restoreEntities(saveData, world);

      // Restore tech states
      this.restoreTechStates(saveData, techSystem);

      // Restore vision states
      this.restoreVisionStates(saveData, visionSystem, saveData.playerFaction);

      // Re-register AI for non-player factions
      this.restoreAI(saveData, factionManager, aiController);

      // Set game phase
      store.setPhase(saveData.phase as 'playing' | 'paused' | 'menu' | 'victory' | 'defeat');

      return true;
    } catch (error) {
      console.error('Failed to deserialize save:', error);
      return false;
    }
  }

  private static restoreFactionStates(saveData: SaveData, factionManager: FactionManager): void {
    for (const state of saveData.factionStates) {
      const current = factionManager.getFactionState(state.id);
      if (current) {
        // Reset gold to saved value
        const goldDiff = state.gold - current.gold;
        if (goldDiff > 0) {
          factionManager.addGold(state.id, goldDiff);
        } else if (goldDiff < 0) {
          factionManager.spendGold(state.id, -goldDiff);
        }

        // Set defeated state
        if (state.isDefeated) {
          factionManager.setDefeated(state.id);
        }

        // Set player faction
        if (state.isPlayer) {
          factionManager.setPlayerFaction(state.id);
        }
      }
    }
  }

  private static restoreEntities(saveData: SaveData, world: World): void {
    // Create entities from saved data
    for (const savedEntity of saveData.entities) {
      if (savedEntity.type === 'unit') {
        this.restoreUnit(savedEntity, world);
      } else if (savedEntity.type === 'building') {
        this.restoreBuilding(savedEntity, world);
      }
    }
  }

  private static restoreUnit(saved: SavedEntity, world: World): void {
    if (!saved.unitType) return;

    const entity = createUnitEntity(
      world,
      saved.unitType as UnitType,
      saved.factionId,
      saved.position.x,
      saved.position.y,
      saved.position.z
    );

    // Restore health
    const health = world.getComponent<HealthComponent>(entity, ComponentTypes.Health);
    if (health) {
      health.current = saved.health.current;
      health.max = saved.health.max;
    }
  }

  private static restoreBuilding(saved: SavedEntity, world: World): void {
    if (!saved.buildingType) return;

    const entity = createBuildingEntity(
      world,
      saved.buildingType as BuildingType,
      saved.factionId,
      saved.position.x,
      saved.position.y,
      saved.position.z
    );

    // Restore health
    const health = world.getComponent<HealthComponent>(entity, ComponentTypes.Health);
    if (health) {
      health.current = saved.health.current;
      health.max = saved.health.max;
    }

    // Restore spawner data
    if (saved.spawnerData) {
      const spawner = world.getComponent<SpawnerComponent>(entity, ComponentTypes.Spawner);
      if (spawner) {
        spawner.spawnTimer = saved.spawnerData.currentCooldown;
        spawner.rallyX = saved.spawnerData.rallyX;
        spawner.rallyZ = saved.spawnerData.rallyZ;
      }
    }
  }

  private static restoreTechStates(saveData: SaveData, techSystem: TechSystem): void {
    for (const techState of saveData.techStates) {
      // Initialize faction tech state
      techSystem.initFaction(techState.factionId);

      // Restore researched techs
      if (techState.researched.length > 0) {
        techSystem.setResearchedTechs(techState.factionId, techState.researched);
      }

      // Restore locked techs (if available in save data)
      if (techState.lockedTechs && techState.lockedTechs.length > 0) {
        techSystem.setLockedTechs(techState.factionId, techState.lockedTechs);
      }

      // Restore current research with progress
      if (techState.currentResearch) {
        techSystem.setResearchProgress(
          techState.factionId,
          techState.currentResearch.techId,
          techState.currentResearch.progress
        );
      }
    }
  }

  private static restoreVisionStates(
    saveData: SaveData,
    visionSystem: VisionSystem,
    playerFaction: FactionId
  ): void {
    // Initialize vision for all factions
    for (const visionState of saveData.visionStates) {
      visionSystem.initFaction(visionState.factionId);
    }

    // Set player faction for vision
    visionSystem.setPlayerFaction(playerFaction);
  }

  private static restoreAI(
    saveData: SaveData,
    factionManager: FactionManager,
    aiController: AIController
  ): void {
    const factionIds: FactionId[] = ['yan', 'han', 'lin', 'shang', 'xuan', 'tie', 'yun'];

    for (const factionId of factionIds) {
      const state = factionManager.getFactionState(factionId);
      if (state && !state.isPlayer && !state.isDefeated) {
        aiController.registerAI(factionId);
      }
    }
  }
}
